import {
  animateStreamLainDropdownPanelClose,
  decorateStreamLainDropdownPanel,
  getStreamLainDropdownMenuChromeCss,
  STREAMLAIN_DROPDOWN_MENU_PORTAL_Z_INDEX,
} from '@affine/core/components/dropdown-panel-chrome/chrome';
import { I18n } from '@affine/i18n';
import { createLitPortal } from '@blocksuite/affine/components/portal';
import { SignalWatcher, WithDisposable } from '@blocksuite/affine/global/lit';
import { ShadowlessElement } from '@blocksuite/affine/std';
import {
  renderStreamLainDisclosureTrigger,
  renderStreamLainNestedDisplayItemRow,
  STREAMLAIN_DISCLOSURE_OVERLAY_ENTER_ANIMATION,
  streamLainDisclosurePopoverShellStyles,
  streamLainDisclosureStyles,
} from '@blocksuite/affine-shared/utils';
import {
  AttachmentIcon,
  CodeBlockIcon,
  DatabaseTableViewIcon,
  EmailIcon,
  PlayIcon,
  ToolIcon,
  UngroupIcon,
} from '@blocksuite/icons/lit';
import { flip, shift } from '@floating-ui/dom';
import { cssVarV2 } from '@streamlain/theme/v2';
import {
  css,
  html,
  LitElement,
  nothing,
  type TemplateResult,
  unsafeCSS,
} from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';

import type { MailMessageAttachment } from '../../utils/mail-selection-context';
import type {
  AutomationAttachment,
  CanvasSelectionItem,
  CodeSelectionAttachment,
  DatabaseAttachment,
  PdfContextAttachment,
  WorkflowAttachment,
} from '../ai-chat-content/type';
import {
  streamLainDropdownMenuOffsetMiddleware,
  streamLainDropdownMenuSizeMiddleware,
} from '../ai-chat-input/dropdown-menu-chrome';
import type { CanonicalMessageContextItem } from './canonical-message-context';
import {
  getChatHistoryPortalBoundary,
  getChatHistoryPortalOverlayWidth,
  getChatHistoryPortalStyle,
  getChatInputPortalContainer,
  getChatInputPortalOverlayWidth,
  getChatInputPortalReferenceElement,
  getChatInputPortalStyle,
} from './chat-input-portal-style';
import {
  getDatabaseAttachmentLabel,
  getDatabaseAttachmentMeta,
} from './database-context-labels';
import {
  flattenCanvasItems,
  getCanvasSelectionIcon,
  isAtomicCanvasItem,
} from './selected-chip';
import type {
  AttachmentChip,
  ChatChip,
  CollectionChip,
  DocChip,
  FileChip,
  SelectedContextChip,
  TagChip,
} from './type';
import {
  getWorkflowAttachmentLabel,
  getWorkflowAttachmentMeta,
} from './workflow-attachment-label';

export type ContextGroupItem =
  | {
      kind: 'database';
      key: string;
      label: string;
      meta: string;
      removable: boolean;
      database: DatabaseAttachment;
      children?: never;
    }
  | {
      kind: 'pdf';
      key: string;
      label: string;
      meta: string;
      removable: boolean;
      pdf: PdfContextAttachment;
      children?: never;
    }
  | {
      kind: 'automation';
      key: string;
      label: string;
      meta: string;
      removable: boolean;
      automation: AutomationAttachment;
      children?: never;
    }
  | {
      kind: 'workflow';
      key: string;
      label: string;
      meta: string;
      removable: boolean;
      workflow: WorkflowAttachment;
      children?: never;
    }
  | {
      kind: 'mail';
      key: string;
      label: string;
      meta: string;
      removable: boolean;
      mail: MailMessageAttachment;
      children?: never;
    }
  | {
      kind: 'code';
      key: string;
      label: string;
      meta: string;
      removable: boolean;
      codeSelection: CodeSelectionAttachment;
      children?: never;
    }
  | {
      kind: 'canvas';
      key: string;
      label: string;
      meta: string;
      removable: boolean;
      chip: SelectedContextChip;
      children: CanvasSelectionItem[];
    }
  | {
      kind: 'generic';
      key: string;
      label: string;
      meta: string;
      removable: boolean;
      contextItem: CanonicalMessageContextItem;
      children?: never;
    }
  | {
      kind: 'doc-chip';
      key: string;
      label: string;
      meta: string;
      removable: boolean;
      chip: DocChip;
      children?: never;
    }
  | {
      kind: 'file-chip';
      key: string;
      label: string;
      meta: string;
      removable: boolean;
      chip: FileChip;
      children?: never;
    }
  | {
      kind: 'attachment-chip';
      key: string;
      label: string;
      meta: string;
      removable: boolean;
      chip: AttachmentChip;
      children?: never;
    }
  | {
      kind: 'tag-chip';
      key: string;
      label: string;
      meta: string;
      removable: boolean;
      chip: TagChip;
      children?: never;
    }
  | {
      kind: 'collection-chip';
      key: string;
      label: string;
      meta: string;
      removable: boolean;
      chip: CollectionChip;
      children?: never;
    };

let contextGroupOverlayIdSeq = 0;

const CONTEXT_GROUP_MIN_WIDTH = 180;
const CONTEXT_GROUP_MAX_WIDTH = 420;

function estimateTextWidth(text: string, charWidth: number): number {
  return Math.ceil(text.length * charWidth);
}

function estimateCanvasItemWidth(
  item: CanvasSelectionItem,
  depth: number
): number {
  const ownWidth =
    20 +
    10 +
    Math.max(
      estimateTextWidth(item.label, 7.5),
      estimateTextWidth(
        [item.childCount ? `${item.childCount} contained` : '', item.text]
          .filter(Boolean)
          .join(' · '),
        6.5
      )
    ) +
    10 +
    24 +
    16 +
    Math.max(0, depth) * 14;
  const childWidth =
    isAtomicCanvasItem(item) || !item.children?.length
      ? 0
      : Math.max(
          ...item.children.map(child =>
            estimateCanvasItemWidth(child, depth + 1)
          )
        );

  return Math.max(ownWidth, childWidth);
}

function estimateContextGroupItemWidth(item: ContextGroupItem): number {
  const actionWidth = item.removable ? 24 : 0;
  const ownWidth =
    20 +
    10 +
    Math.max(
      estimateTextWidth(item.label, 7.5),
      estimateTextWidth(item.meta, 6.5)
    ) +
    10 +
    actionWidth +
    16;

  if (item.kind !== 'canvas') {
    return ownWidth;
  }

  const childWidth = item.children.length
    ? Math.max(...item.children.map(child => estimateCanvasItemWidth(child, 1)))
    : 0;

  return Math.max(ownWidth, childWidth);
}

function getContextGroupPreferredWidth(items: ContextGroupItem[]): number {
  if (!items.length) {
    return CONTEXT_GROUP_MIN_WIDTH;
  }

  return Math.min(
    CONTEXT_GROUP_MAX_WIDTH,
    Math.max(
      CONTEXT_GROUP_MIN_WIDTH,
      Math.max(...items.map(estimateContextGroupItemWidth))
    )
  );
}

function getContextGroupTriggerLabel(items: ContextGroupItem[]): string {
  if (items.length === 1) {
    return (
      items[0].label ||
      I18n.t('com.streamlain.ai.context.group.label', { count: 1 })
    );
  }

  return I18n.t('com.streamlain.ai.context.group.label', {
    count: items.length,
  });
}

function getContextGroupTriggerTitle(items: ContextGroupItem[]): string {
  if (items.length === 1) {
    return [items[0].label, items[0].meta].filter(Boolean).join(' · ');
  }

  return I18n.t('com.streamlain.ai.context.group.tooltip', {
    count: items.length,
  });
}

function getCanvasChildren(chip: SelectedContextChip): CanvasSelectionItem[] {
  return (chip.canvasSelections || []).flatMap(selection => selection.items);
}

function hasCanvasDetails(children: CanvasSelectionItem[]): boolean {
  if (children.length === 1 && isAtomicCanvasItem(children[0])) {
    return false;
  }

  return flattenCanvasItems(children).length > 1;
}

function getGenericContextMeta(item: CanonicalMessageContextItem): string {
  const unavailableReason = item.display.unavailable
    ? item.display.unavailableReason || 'Unavailable'
    : null;
  return [
    ...new Set(
      [
        unavailableReason,
        item.display.subtitle,
        item.subtitle,
        item.kind.toLowerCase(),
      ].filter(Boolean)
    ),
  ].join(' · ');
}

export function buildContextGroupItems(input: {
  attachmentChips?: AttachmentChip[];
  collectionChips?: Array<{
    chip: CollectionChip;
    label: string;
    meta: string;
  }>;
  databaseAttachments: DatabaseAttachment[];
  docChips?: Array<{ chip: DocChip; label: string; meta: string }>;
  fileChips?: FileChip[];
  pdfContextAttachments: PdfContextAttachment[];
  automationAttachments: AutomationAttachment[];
  workflowAttachments: WorkflowAttachment[];
  codeSelections: CodeSelectionAttachment[];
  mailAttachments: MailMessageAttachment[];
  selectedContextChips: SelectedContextChip[];
  tagChips?: Array<{ chip: TagChip; label: string; meta: string }>;
  genericContextItems?: CanonicalMessageContextItem[];
}): ContextGroupItem[] {
  return [
    ...(input.genericContextItems ?? []).map(contextItem => ({
      kind: 'generic' as const,
      key: `generic:${contextItem.kind}:${contextItem.id}`,
      label: contextItem.display.title || contextItem.label,
      meta: getGenericContextMeta(contextItem),
      removable: false,
      contextItem,
    })),
    ...(input.docChips ?? []).map(({ chip, label, meta }) => ({
      kind: 'doc-chip' as const,
      key: `doc-chip:${chip.docId}`,
      label,
      meta,
      removable: true,
      chip,
    })),
    ...(input.fileChips ?? []).map(chip => ({
      kind: 'file-chip' as const,
      key: `file-chip:${chip.fileId ?? chip.blobId ?? chip.file.name}`,
      label: chip.file.name,
      meta: [chip.file.type || 'file', chip.state].filter(Boolean).join(' · '),
      removable: true,
      chip,
    })),
    ...(input.attachmentChips ?? []).map(chip => ({
      kind: 'attachment-chip' as const,
      key: `attachment-chip:${chip.sourceId}`,
      label: chip.name,
      meta: ['attachment', chip.state].filter(Boolean).join(' · '),
      removable: true,
      chip,
    })),
    ...(input.tagChips ?? []).map(({ chip, label, meta }) => ({
      kind: 'tag-chip' as const,
      key: `tag-chip:${chip.tagId}`,
      label,
      meta,
      removable: true,
      chip,
    })),
    ...(input.collectionChips ?? []).map(({ chip, label, meta }) => ({
      kind: 'collection-chip' as const,
      key: `collection-chip:${chip.collectionId}`,
      label,
      meta,
      removable: true,
      chip,
    })),
    ...input.databaseAttachments.map(database => ({
      kind: 'database' as const,
      key: `database:${database.blockId}`,
      label: getDatabaseAttachmentLabel(database),
      meta: getDatabaseAttachmentMeta(database),
      removable: true,
      database,
    })),
    ...input.pdfContextAttachments.map(pdf => ({
      kind: 'pdf' as const,
      key: `pdf:${pdf.blockId}`,
      label: pdf.name,
      meta: I18n.t('com.streamlain.ai.context.pdf'),
      removable: true,
      pdf,
    })),
    ...input.automationAttachments.map(automation => ({
      kind: 'automation' as const,
      key: `automation:${automation.blockId}`,
      label: automation.name,
      meta: automation.sourceTableName
        ? `${I18n.t('com.streamlain.ai.context.automation')} · ${automation.sourceTableName}`
        : I18n.t('com.streamlain.ai.context.automation'),
      removable: true,
      automation,
    })),
    ...input.workflowAttachments.map(workflow => ({
      kind: 'workflow' as const,
      key: `workflow:${workflow.workflowId}`,
      label: getWorkflowAttachmentLabel(workflow),
      meta: `${I18n.t('com.streamlain.ai.context.workflow')} · ${getWorkflowAttachmentMeta(workflow)}`,
      removable: true,
      workflow,
    })),
    ...input.mailAttachments.map(mail => ({
      kind: 'mail' as const,
      key: `mail:${mail.id}`,
      label: mail.subject || mail.sender || 'Mail',
      meta: [mail.sender, mail.dateLabel].filter(Boolean).join(' · '),
      removable: true,
      mail,
    })),
    ...input.codeSelections.map(codeSelection => ({
      kind: 'code' as const,
      key: `code:${codeSelection.id}`,
      label: getCodeSelectionLabel(codeSelection),
      meta: getCodeSelectionMeta(codeSelection),
      removable: true,
      codeSelection,
    })),
    ...input.selectedContextChips.map(chip => {
      const children = getCanvasChildren(chip);
      const flatChildren = flattenCanvasItems(children);
      return {
        kind: 'canvas' as const,
        key: `canvas:${chip.uuid}`,
        label: chip.name ?? I18n.t('com.streamlain.ai.context.canvas.default'),
        meta:
          flatChildren.length > children.length
            ? I18n.t('com.streamlain.ai.context.selected-contained', {
                selected: children.length,
                total: flatChildren.length,
              })
            : I18n.t('com.streamlain.ai.context.selected', {
                count: children.length,
              }),
        removable: true,
        chip,
        children,
      };
    }),
  ];
}

function getCodeSelectionLabel(selection: CodeSelectionAttachment): string {
  const lines =
    selection.fromLine === selection.toLine
      ? `L${selection.fromLine}`
      : `L${selection.fromLine}-L${selection.toLine}`;
  return `${selection.title} · ${lines}`;
}

function getCodeSelectionMeta(selection: CodeSelectionAttachment): string {
  return `${selection.language} · ${selection.surface}`;
}

export class ChatPanelContextGroupPortal extends LitElement {
  static override styles = [
    unsafeCSS(streamLainDisclosureStyles),
    css`
      :host {
        display: block;
        width: max-content;
        max-width: 100%;
        color: ${unsafeCSS(cssVarV2.text.primary)};
        box-sizing: border-box;
      }

      :host([inline]) {
        width: max-content;
        max-width: 100%;
      }

      :host([inline]) .context-group-portal {
        width: min(
          var(--context-group-preferred-width, 420px),
          var(--streamlain-disclosure-overlay-width, 420px),
          calc(100vw - 32px)
        );
        max-width: 100%;
        max-height: none;
        overflow: visible;
        padding: 0;
        border: 0;
        border-radius: 0;
        background: transparent;
        box-shadow: none;
        animation: none;
      }

      :host([inline]) .context-group-list {
        padding: 8px;
      }

      .context-group-list {
        display: flex;
        flex-direction: column;
        gap: 0;
        max-height: none;
        overflow: visible;
        scrollbar-gutter: auto;
      }

      .context-group-portal {
        width: min(
          var(--context-group-preferred-width, 420px),
          var(--streamlain-disclosure-overlay-width, 420px),
          calc(100vw - 32px)
        );
        max-width: 100%;
        padding: 8px;
        border-radius: 8px;
        border: 0.5px solid ${unsafeCSS(cssVarV2.layer.insideBorder.border)};
        background: var(--streamlain-background-primary-color);
        box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
        box-sizing: border-box;
        transform-origin: bottom right;
        animation: ${unsafeCSS(STREAMLAIN_DISCLOSURE_OVERLAY_ENTER_ANIMATION)};
      }

      :host([input-surface]) {
        width: min(
          var(--streamlain-disclosure-overlay-width, 420px),
          calc(100vw - 32px)
        );
      }

      :host([input-surface]) .context-group-portal {
        width: min(
          var(--streamlain-disclosure-overlay-width, 420px),
          calc(100vw - 32px)
        );
        border: 0;
        background: transparent;
        box-shadow: none;
        color: var(--streamlain-text-primary-color);
        animation: none;
      }

      :host([input-surface]) .streamlain-display-row,
      :host([input-surface]) .streamlain-display-row-title,
      :host([input-surface]) .streamlain-display-row-leading-label,
      :host([input-surface]) .streamlain-nested-display-row,
      :host([input-surface]) .streamlain-nested-display-title,
      :host([input-surface]) .streamlain-nested-display-leading-label {
        color: var(--streamlain-text-primary-color);
      }

      :host([input-surface]) .streamlain-display-row-subtitle,
      :host([input-surface]) .streamlain-display-row-preview,
      :host([input-surface]) .streamlain-nested-display-subtitle,
      :host([input-surface]) .streamlain-nested-display-preview {
        color: var(--streamlain-text-secondary-color);
      }

      :host([input-surface]) .streamlain-display-row-icon,
      :host([input-surface]) .streamlain-nested-display-icon,
      :host([input-surface]) .streamlain-nested-display-chevron,
      :host([input-surface]) .context-group-remove {
        color: var(--streamlain-v2-icon-secondary);
      }

      :host([input-surface]) .streamlain-display-row-clickable:hover,
      :host([input-surface]) .streamlain-display-row-clickable:focus-visible,
      :host([input-surface]) .streamlain-nested-display-row-clickable:hover,
      :host([input-surface])
        .streamlain-nested-display-row-clickable:focus-visible,
      :host([input-surface]) .context-group-remove:hover,
      :host([input-surface]) .context-group-remove:focus-visible {
        background: var(--streamlain-v2-layer-background-hoverOverlay);
      }

      :host([input-surface]) .streamlain-nested-display-row[data-depth]::before,
      :host([input-surface]) .streamlain-nested-display-tree-guide {
        background: var(
          --streamlain-v2-layer-insideBorder-border,
          ${unsafeCSS(cssVarV2.layer.insideBorder.border)}
        );
      }

      :host([input-surface]) .streamlain-nested-display-row[data-depth]::after {
        border-color: var(
          --streamlain-v2-layer-insideBorder-border,
          ${unsafeCSS(cssVarV2.layer.insideBorder.border)}
        );
      }

      .context-group-remove {
        width: 24px;
        height: 24px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: 0;
        border-radius: 4px;
        background: transparent;
        color: ${unsafeCSS(cssVarV2.icon.secondary)};
        cursor: pointer;
        font-size: 18px;
        line-height: 1;
        opacity: 0.72;
        transition:
          background 0.12s ease,
          color 0.12s ease,
          opacity 0.12s ease,
          transform 0.12s ease;
      }

      .context-group-remove:hover,
      .context-group-remove:focus-visible {
        background: ${unsafeCSS(cssVarV2.layer.background.hoverOverlay)};
        color: var(--streamlain-error-color);
        opacity: 1;
        outline: none;
        transform: scale(1.06);
      }

      .context-group-remove:active {
        transform: scale(0.94);
      }

      .context-group-remove svg {
        width: 16px;
        height: 16px;
      }
    `,
  ];

  @property({ attribute: false })
  accessor items: ContextGroupItem[] = [];

  @property({ attribute: false })
  accessor readOnly = false;

  @property({ type: Boolean, reflect: true })
  accessor inline = false;

  @property({ type: Boolean, reflect: true, attribute: 'input-surface' })
  accessor inputSurface = false;

  @state()
  private accessor _expandedCanvasGroups = new Set<string>();

  private _canvasGroupKey(
    item: ContextGroupItem & { kind: 'canvas' },
    child: CanvasSelectionItem
  ): string {
    return `${item.key}:${child.id}`;
  }

  private _toggleCanvasGroup(groupKey: string) {
    const expandedCanvasGroups = new Set(this._expandedCanvasGroups);

    if (expandedCanvasGroups.has(groupKey)) {
      expandedCanvasGroups.delete(groupKey);
    } else {
      expandedCanvasGroups.add(groupKey);
    }

    this._expandedCanvasGroups = expandedCanvasGroups;
    void this.updateComplete.then(() => this._notifyLayoutChange());
  }

  private _notifyLayoutChange() {
    const dispatch = () => {
      this.dispatchEvent(
        new CustomEvent('context-group-layout-change', {
          bubbles: true,
          composed: true,
        })
      );
    };

    dispatch();
    requestAnimationFrame(() => {
      dispatch();
      window.setTimeout(dispatch, 180);
    });
  }

  private _iconFor(item: ContextGroupItem) {
    switch (item.kind) {
      case 'database':
        return DatabaseTableViewIcon();
      case 'pdf':
        return AttachmentIcon();
      case 'automation':
        return PlayIcon();
      case 'workflow':
        return ToolIcon();
      case 'mail':
        return EmailIcon();
      case 'code':
        return CodeBlockIcon();
      case 'canvas':
        return UngroupIcon();
      case 'generic':
      case 'doc-chip':
      case 'file-chip':
      case 'attachment-chip':
      case 'tag-chip':
      case 'collection-chip':
        return AttachmentIcon();
    }
  }

  private _remove(
    kind: ContextGroupItem['kind'],
    key: string,
    itemId?: string,
    itemIds?: string[]
  ) {
    this.dispatchEvent(
      new CustomEvent('remove-context-item', {
        detail: { kind, key, itemId, itemIds },
        bubbles: true,
        composed: true,
      })
    );
  }

  private _renderRemoveButton(
    item: ContextGroupItem,
    itemId?: string,
    itemIds?: string[]
  ): TemplateResult<1> | typeof nothing {
    if (!item.removable || this.readOnly) {
      return nothing;
    }

    return html`<button
      class="context-group-remove"
      aria-label=${I18n.t('com.streamlain.ai.context.remove')}
      @click=${(event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        this._remove(item.kind, item.key, itemId, itemIds);
      }}
    >
      <span aria-hidden="true">×</span>
    </button>`;
  }

  private _renderCanvasChild(
    item: ContextGroupItem & { kind: 'canvas' },
    child: CanvasSelectionItem,
    index: number,
    siblings: CanvasSelectionItem[],
    treeGuideDepths: number[] = []
  ): TemplateResult {
    const depth = Math.min((child.depth ?? 0) + 1, 8);
    const hasChildren = Boolean(
      !isAtomicCanvasItem(child) && child.children?.length
    );
    const groupKey = this._canvasGroupKey(item, child);
    const expanded = hasChildren && this._expandedCanvasGroups.has(groupKey);
    const hasNextSibling = index < siblings.length - 1;
    const childTreeGuideDepths =
      hasNextSibling && depth > 0
        ? [...treeGuideDepths, depth]
        : treeGuideDepths;
    const metaParts = [
      child.childCount
        ? I18n.t('com.streamlain.ai.context.contained', {
            count: child.childCount,
          })
        : null,
      child.text,
    ].filter(Boolean);

    const row = renderStreamLainNestedDisplayItemRow({
      clickSurface: 'div',
      className: 'context-group-canvas-child',
      depth,
      expanded: hasChildren ? expanded : undefined,
      icon: getCanvasSelectionIcon(child),
      onClick: hasChildren
        ? event => {
            event.preventDefault();
            this._toggleCanvasGroup(groupKey);
          }
        : undefined,
      title: child.label,
      subtitle: metaParts.join(' · '),
      titleAttr: child.text || child.label,
      treeGuideDepths,
      treeLast: index === siblings.length - 1,
      treeToggleStyle: 'plus-circle',
      action: this._renderRemoveButton(item, child.id),
    });

    if (!hasChildren) {
      return row;
    }

    return html`<div
      class="streamlain-nested-display-group context-group-canvas-group"
      data-open=${expanded ? 'true' : 'false'}
    >
      ${row}
      <div
        class="streamlain-nested-display-group-body"
        aria-hidden=${expanded ? 'false' : 'true'}
        ?inert=${!expanded}
      >
        <div class="streamlain-nested-display-group-body-inner">
          ${child.children?.map((nestedChild, nestedIndex, nestedSiblings) =>
            this._renderCanvasChild(
              item,
              nestedChild,
              nestedIndex,
              nestedSiblings,
              childTreeGuideDepths
            )
          )}
        </div>
      </div>
    </div>`;
  }

  private _renderCanvasRoot(item: ContextGroupItem & { kind: 'canvas' }) {
    const showCanvasDetails = hasCanvasDetails(item.children);
    const row = renderStreamLainNestedDisplayItemRow({
      icon: this._iconFor(item),
      title: item.label,
      subtitle: item.meta,
      titleAttr: item.label,
      action: this._renderRemoveButton(
        item,
        item.children.length === 1 ? item.children[0].id : undefined,
        item.children.length > 1
          ? item.children.map(child => child.id)
          : undefined
      ),
    });

    if (!showCanvasDetails) {
      return row;
    }

    return html`
      ${row}
      ${item.children.map((child, index, siblings) =>
        this._renderCanvasChild(item, child, index, siblings)
      )}
    `;
  }

  private _renderRow(item: ContextGroupItem): TemplateResult {
    if (item.kind === 'canvas') {
      return this._renderCanvasRoot(item);
    }

    return html`
      ${renderStreamLainNestedDisplayItemRow({
        icon: this._iconFor(item),
        title: item.label,
        subtitle: item.meta,
        titleAttr: item.label,
        action: this._renderRemoveButton(item),
      })}
    `;
  }

  override render() {
    const preferredWidth = getContextGroupPreferredWidth(this.items);

    return html`<div
      class="context-group-portal streamlain-disclosure-popover-scroll"
      style=${`--context-group-preferred-width:${preferredWidth}px`}
    >
      <div class="context-group-list streamlain-nested-display-list">
        ${this.items.map(item => this._renderRow(item))}
      </div>
    </div>`;
  }
}

export class ChatPanelContextGroupChip extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = [
    unsafeCSS(streamLainDisclosureStyles),
    css`
      :host {
        display: inline-flex;
        max-width: 100%;
        min-width: 0;
      }

      .context-group-input-trigger {
        max-width: 100%;
      }

      .streamlain-disclosure-trigger-card.context-group-trigger-popover-up[aria-expanded='true']
        .streamlain-inline-disclosure-chevron {
        transform: rotate(-90deg);
      }

      .streamlain-disclosure-trigger-card.context-group-trigger-popover-down[aria-expanded='true']
        .streamlain-inline-disclosure-chevron {
        transform: rotate(90deg);
      }

      .context-group-input-shell,
      .context-group-history-shell {
        max-width: 100%;
      }

      .context-group-portal-popover chat-panel-context-group-portal {
        display: block;
        width: 100%;
        max-width: 100%;
      }
    `,
  ];

  @property({ attribute: false })
  accessor databaseAttachments: DatabaseAttachment[] = [];

  @property({ attribute: false })
  accessor pdfContextAttachments: PdfContextAttachment[] = [];

  @property({ attribute: false })
  accessor automationAttachments: AutomationAttachment[] = [];

  @property({ attribute: false })
  accessor workflowAttachments: WorkflowAttachment[] = [];

  @property({ attribute: false })
  accessor codeSelections: CodeSelectionAttachment[] = [];

  @property({ attribute: false })
  accessor mailAttachments: MailMessageAttachment[] = [];

  @property({ attribute: false })
  accessor selectedContextChips: SelectedContextChip[] = [];

  @property({ attribute: false })
  accessor genericContextItems: CanonicalMessageContextItem[] = [];

  @property({ attribute: false })
  accessor docChips: Array<{ chip: DocChip; label: string; meta: string }> = [];

  @property({ attribute: false })
  accessor fileChips: FileChip[] = [];

  @property({ attribute: false })
  accessor attachmentChips: AttachmentChip[] = [];

  @property({ attribute: false })
  accessor tagChips: Array<{ chip: TagChip; label: string; meta: string }> = [];

  @property({ attribute: false })
  accessor collectionChips: Array<{
    chip: CollectionChip;
    label: string;
    meta: string;
  }> = [];

  @property({ attribute: false })
  accessor readOnly = false;

  @property({ attribute: false })
  accessor inlineExpandable = false;

  @property({ attribute: false })
  accessor portalContainer: HTMLElement | null = null;

  @property({ attribute: false })
  accessor removeDatabaseAttachment:
    | ((database: DatabaseAttachment) => void)
    | undefined;

  @property({ attribute: false })
  accessor removePdfContextAttachment:
    | ((pdf: PdfContextAttachment) => void)
    | undefined;

  @property({ attribute: false })
  accessor removeAutomationAttachment:
    | ((automation: AutomationAttachment) => void)
    | undefined;

  @property({ attribute: false })
  accessor removeWorkflowAttachment:
    | ((workflow: WorkflowAttachment) => void)
    | undefined;

  @property({ attribute: false })
  accessor removeCodeSelection:
    | ((selection: CodeSelectionAttachment) => void)
    | undefined;

  @property({ attribute: false })
  accessor removeMailAttachment:
    | ((mail: MailMessageAttachment) => void)
    | undefined;

  @property({ attribute: false })
  accessor removeChip!: (chip: ChatChip) => Promise<void>;

  @property({ attribute: false })
  accessor removeCanvasSelectionItem:
    | ((chip: ChatChip, itemId: string) => void)
    | undefined;

  @property({ attribute: false })
  accessor removeCanvasSelectionItems:
    | ((chip: ChatChip, itemIds: string[]) => void)
    | undefined;

  @query('.context-group-input-trigger')
  accessor chipElement!: HTMLElement;

  private _abortController: AbortController | null = null;
  private readonly _overlayId = `context-group-overlay-${++contextGroupOverlayIdSeq}`;
  private _closingPortal = false;
  private _overlayResizeObserver: ResizeObserver | null = null;
  private _portalPositionUpdate: (() => void) | null = null;
  private _portalRenderUpdate: (() => void) | null = null;
  private _portalRoot: HTMLElement | null = null;

  @state()
  accessor portalDirection: 'up' | 'down' = 'up';

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._closePortal({ animate: false });
  }

  private _items() {
    return buildContextGroupItems({
      attachmentChips: this.attachmentChips,
      collectionChips: this.collectionChips,
      databaseAttachments: this.databaseAttachments,
      docChips: this.docChips,
      fileChips: this.fileChips,
      pdfContextAttachments: this.pdfContextAttachments,
      automationAttachments: this.automationAttachments,
      workflowAttachments: this.workflowAttachments,
      codeSelections: this.codeSelections,
      mailAttachments: this.mailAttachments,
      selectedContextChips: this.selectedContextChips,
      tagChips: this.tagChips,
      genericContextItems: this.genericContextItems,
    });
  }

  override render() {
    const items = this._items();
    const label = getContextGroupTriggerLabel(items);
    const title = getContextGroupTriggerTitle(items);
    const expanded = Boolean(this._abortController && !this._closingPortal);
    const portalDirection = expanded
      ? this.portalDirection
      : this._preferredPortalDirection();
    const triggerClass = [
      'context-group-input-trigger',
      `context-group-trigger-popover-${portalDirection}`,
    ]
      .filter(Boolean)
      .join(' ');
    const shellClass = [
      this.inlineExpandable
        ? 'context-group-history-shell'
        : 'context-group-input-shell',
      'streamlain-disclosure-overlay-anchor',
      portalDirection === 'down'
        ? 'streamlain-disclosure-overlay-down'
        : 'streamlain-disclosure-overlay-up',
    ].join(' ');

    return html`<span class=${shellClass}>
      ${renderStreamLainDisclosureTrigger({
        className: triggerClass,
        controlsId: this._overlayId,
        expanded,
        summary: label,
        title,
        onClick: this._handleChipClick,
      })}
    </span>`;
  }

  private _closePortal({ animate = true }: { animate?: boolean } = {}) {
    const abortController = this._abortController;
    if (!abortController || this._closingPortal) return;

    const finishClose = () => {
      abortController.abort();
    };

    const portalElement = this._getPortalElement();
    if (animate && portalElement?.isConnected) {
      this._closingPortal = true;
      this.requestUpdate();
      animateStreamLainDropdownPanelClose(portalElement, finishClose);
      return;
    }

    finishClose();
  }

  private _removeContextItem(detail: {
    kind: ContextGroupItem['kind'];
    key: string;
    itemId?: string;
    itemIds?: string[];
  }) {
    if (this.readOnly) return;
    const item = this._items().find(item => item.key === detail.key);
    if (!item) return;
    switch (item.kind) {
      case 'database':
        this.removeDatabaseAttachment?.(item.database);
        break;
      case 'pdf':
        this.removePdfContextAttachment?.(item.pdf);
        break;
      case 'automation':
        this.removeAutomationAttachment?.(item.automation);
        break;
      case 'workflow':
        this.removeWorkflowAttachment?.(item.workflow);
        break;
      case 'code':
        this.removeCodeSelection?.(item.codeSelection);
        break;
      case 'mail':
        this.removeMailAttachment?.(item.mail);
        break;
      case 'canvas':
        if (detail.itemId) {
          this.removeCanvasSelectionItem?.(item.chip, detail.itemId);
        } else if (detail.itemIds?.length) {
          this.removeCanvasSelectionItems?.(item.chip, detail.itemIds);
        } else {
          this.removeChip(item.chip).catch(console.error);
        }
        break;
      case 'generic':
        break;
      case 'doc-chip':
      case 'file-chip':
      case 'attachment-chip':
      case 'tag-chip':
      case 'collection-chip':
        this.removeChip(item.chip).catch(console.error);
        break;
    }
    requestAnimationFrame(() => {
      if (this._items().length === 0) {
        this._closePortal();
      } else {
        this.requestUpdate();
        this._portalRenderUpdate?.();
        this._syncOverlayGeometry();
      }
    });
  }

  private readonly _syncOverlayGeometry = () => {
    if (!this._abortController || !this.chipElement) return;

    this._portalRenderUpdate?.();
    this._portalPositionUpdate?.();
    requestAnimationFrame(() => this._decoratePortalElement());
  };

  private readonly _handlePortalLayoutChange = () => {
    this._syncOverlayGeometry();
  };

  private _portalWidth() {
    if (!this.inlineExpandable) {
      return getChatInputPortalOverlayWidth(this._getPortalReferenceElement());
    }

    return getChatHistoryPortalOverlayWidth(this);
  }

  private _portalStyle() {
    const width = this._portalWidth();
    return this.inlineExpandable
      ? getChatHistoryPortalStyle(this, width)
      : getChatInputPortalStyle(this, width);
  }

  private _preferredPortalDirection(): 'up' | 'down' {
    return this.inlineExpandable ? 'down' : 'up';
  }

  private _portalPlacement() {
    return this.inlineExpandable ? 'bottom-end' : 'top-start';
  }

  private _portalMiddleware() {
    const boundary = this._getOverlayBoundary();
    const offsetMiddleware = streamLainDropdownMenuOffsetMiddleware();

    if (!this.inlineExpandable) {
      return [
        offsetMiddleware,
        streamLainDropdownMenuSizeMiddleware({
          widthBoundary: boundary,
          minHeight: 0,
        }),
        shift({ padding: 16 }),
      ];
    }

    return [
      offsetMiddleware,
      flip({
        fallbackPlacements: ['top-end'],
      }),
      streamLainDropdownMenuSizeMiddleware({ boundary, minHeight: 0 }),
      shift({ boundary }),
    ];
  }

  private _renderPortal() {
    const popoverClass = this.inlineExpandable
      ? 'context-group-history-popover'
      : 'context-group-input-popover';

    return html`<style>
        ${getStreamLainDropdownMenuChromeCss()}
          ${streamLainDisclosurePopoverShellStyles}
          .context-group-portal-popover
          chat-panel-context-group-portal {
          display: block;
          width: 100%;
          max-width: 100%;
        }
      </style>
      <span
        class=${`${popoverClass} context-group-portal-popover streamlain-disclosure-popover-shell`}
        style=${styleMap(this._portalStyle())}
      >
        <chat-panel-context-group-portal
          id=${this._overlayId}
          .items=${this._items()}
          .readOnly=${this.readOnly}
          .inputSurface=${true}
          @context-group-layout-change=${this._handlePortalLayoutChange}
          @remove-context-item=${(
            event: CustomEvent<{
              kind: ContextGroupItem['kind'];
              key: string;
              itemId?: string;
              itemIds?: string[];
            }>
          ) => {
            event.preventDefault();
            event.stopPropagation();
            this._removeContextItem(event.detail);
          }}
        ></chat-panel-context-group-portal>
      </span>`;
  }

  private _getPortalElement(): HTMLElement | null {
    const portalElement =
      this._portalRoot?.shadowRoot?.querySelector<HTMLElement>(
        '.context-group-portal-popover'
      ) ?? null;
    return portalElement;
  }

  private _decoratePortalElement() {
    const portalElement = this._getPortalElement();
    if (!portalElement || !this.chipElement || this._closingPortal) return;

    this._syncPortalDirection(portalElement);
    decorateStreamLainDropdownPanel(portalElement, this.chipElement);
  }

  private _syncPortalDirection(portalElement: HTMLElement) {
    if (!this.inlineExpandable) {
      if (this.portalDirection !== 'up') {
        this.portalDirection = 'up';
      }
      return;
    }

    const portalRect = portalElement.getBoundingClientRect();
    const chipRect = this.chipElement.getBoundingClientRect();
    const portalCenterY = portalRect.top + portalRect.height / 2;
    const chipCenterY = chipRect.top + chipRect.height / 2;
    const nextDirection = portalCenterY < chipCenterY ? 'up' : 'down';
    if (this.portalDirection !== nextDirection) {
      this.portalDirection = nextDirection;
    }
  }

  private _getOverlayBoundary(): Element {
    if (this.inlineExpandable) {
      return getChatHistoryPortalBoundary(this);
    }

    return this._getPortalReferenceElement();
  }

  private _getPortalReferenceElement(): HTMLElement {
    return getChatInputPortalReferenceElement(this, this.chipElement);
  }

  private _getPortalPositionReferenceElement(): HTMLElement {
    return this.chipElement;
  }

  private _getPortalContainer(): Element {
    return getChatInputPortalContainer(this, this.portalContainer);
  }

  private _disconnectOverlayResizeObserver() {
    this._overlayResizeObserver?.disconnect();
    this._overlayResizeObserver = null;
  }

  private _observeOverlayBoundary(signal: AbortSignal) {
    if (typeof ResizeObserver === 'undefined') return;
    this._disconnectOverlayResizeObserver();
    const boundary = this._getOverlayBoundary();
    const resizeObserver = new ResizeObserver(() => {
      this._syncOverlayGeometry();
    });
    resizeObserver.observe(boundary);
    this._overlayResizeObserver = resizeObserver;
    signal.addEventListener(
      'abort',
      () => this._disconnectOverlayResizeObserver(),
      { once: true }
    );
  }

  private readonly _togglePortal = () => {
    const items = this._items();
    if (items.length === 0) return;
    if (this._abortController) {
      this._closePortal();
      return;
    }

    const abortController = new AbortController();
    abortController.signal.addEventListener('abort', () => {
      this._abortController = null;
      this._closingPortal = false;
      this._portalPositionUpdate = null;
      this._portalRenderUpdate = null;
      this._portalRoot = null;
      this.style.removeProperty('--streamlain-disclosure-overlay-width');
      this.style.removeProperty('--streamlain-disclosure-overlay-shift-x');
      this.requestUpdate();
    });
    document.addEventListener(
      'pointerdown',
      event => {
        const path = event.composedPath();
        const portalElement = this._getPortalElement();
        if (
          path.includes(this) ||
          (portalElement && path.includes(portalElement)) ||
          (this._portalRoot && path.includes(this._portalRoot))
        ) {
          return;
        }
        this._closePortal();
      },
      { signal: abortController.signal }
    );
    document.addEventListener(
      'keydown',
      event => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this._closePortal();
        }
      },
      { signal: abortController.signal }
    );
    window.addEventListener('resize', this._syncOverlayGeometry, {
      signal: abortController.signal,
    });
    this.portalDirection = this._preferredPortalDirection();
    this._abortController = abortController;
    this._observeOverlayBoundary(abortController.signal);
    const { portal, update } = createLitPortal({
      template: ({ updatePortal }) => {
        this._portalRenderUpdate = updatePortal;
        return this._renderPortal();
      },
      portalStyles: {
        zIndex: STREAMLAIN_DROPDOWN_MENU_PORTAL_Z_INDEX,
      },
      container: this._getPortalContainer(),
      positionStrategy: 'fixed',
      computePosition: {
        referenceElement: this._getPortalPositionReferenceElement(),
        placement: this._portalPlacement(),
        middleware: this._portalMiddleware(),
        autoUpdate: true,
      },
      abortController,
    });
    this._portalRoot = portal;
    this._portalPositionUpdate = update;
    this.requestUpdate();
    requestAnimationFrame(() => {
      this._decoratePortalElement();
      this._syncOverlayGeometry();
    });
  };

  private readonly _handleChipClick = () => {
    this._togglePortal();
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'chat-panel-context-group-chip': ChatPanelContextGroupChip;
    'chat-panel-context-group-portal': ChatPanelContextGroupPortal;
  }
}
